import { useEffect, useMemo } from 'react';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { customerVehiclesServices, vehiclesServices } from '@services';
import { getCleanedUrlParams, showError } from '@utils';
import { CUSTOMER_VEHICLES_PAGE_SIZE } from 'components/customerVehicles/constants';
import {
  normalizeCustomerVehicles,
  normalizeCustomerVehiclesBrands,
  getSearchRequestPayload,
} from './utils';

export const useCustomerVehiclesOperations = (enableFetch = true) => {
  const router = useRouter();

  const page = router?.query?.page || 1;
  const pageSize = router?.query?.pageSize || CUSTOMER_VEHICLES_PAGE_SIZE;
  const order = router?.query?.order || 'desc';
  const orderBy = router?.query?.orderBy || 'createdAt';
  const query = router?.query?.query;

  const queryParams = {
    page,
    pageSize: parseInt(pageSize, 10),
    order,
    orderBy,
    query,
  };

  const queryParamsCleaned = getCleanedUrlParams(queryParams);

  const customerVehiclesResponse = useQuery(
    [QUERY_KEYS.GET_CUSTOMER_VEHICLES_KEY, queryParamsCleaned, router.query],
    () => {
      const searchPayload = getSearchRequestPayload(router.query);

      return customerVehiclesServices.getCustomerVehicles(
        queryParamsCleaned,
        searchPayload
      );
    },
    { enabled: enableFetch, refetchOnMount: true }
  );

  const customerVehicles = normalizeCustomerVehicles(
    customerVehiclesResponse?.data?.data || []
  );

  const { data: enumsData } = useQuery([QUERY_KEYS.GET_ENUMS_KEY], () =>
    vehiclesServices.getVehiclesEnums()
  );

  const customerVehiclesBrands = useMemo(
    () => normalizeCustomerVehiclesBrands(enumsData?.data?.models),
    [enumsData?.data?.models]
  );
  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_CUSTOMER_VEHICLES_ENUM_KEY],
    () => customerVehiclesServices.getEnums()
  );

  const loading =
    customerVehiclesResponse?.isLoading ||
    customerVehiclesResponse?.isFetching ||
    enumsResponse?.isLoading ||
    enumsResponse?.isFetching;

  useEffect(() => {
    if (customerVehiclesResponse.isError) {
      showError(customerVehiclesResponse?.error);
    }
  }, [customerVehiclesResponse.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (enumsResponse?.error) {
      showError(enumsResponse?.error);
    }
  }, [enumsResponse?.error]);

  return {
    page,
    pageSize: parseInt(pageSize, 10) || CUSTOMER_VEHICLES_PAGE_SIZE,
    total: customerVehiclesResponse?.data?.meta?.total,
    loading,
    customerVehicles,
    customerVehiclesBrands,
    enums: enumsResponse?.data?.data,
    getCustomerVehicles: customerVehiclesResponse.refetch,
  };
};
